import React, { FC, useState } from 'react'
import dayjs from 'dayjs'
import { Button, DatePicker, Flex, Select, InputNumber, Space, Typography, Divider } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Input from 'antd/es/input'
import Icon from 'components/Icon'
import { Filter, FilterTypes } from 'components/FilterDrawer/FilterDrawer.config'
import styles from './STRTablePage.module.scss'
import STRMoreFiltersPopover from './STRMoreFiltersDrawer'

const { RangePicker } = DatePicker

export interface STRFilters {
  yearRange?: [unknown, unknown]
  searchColumn?: string
  searchText?: string
  unreportedRevenueOp?: 'gte' | 'lte'
  unreportedRevenueVal?: number | null
  estimatedRevenueOp?: 'gte' | 'lte'
  estimatedRevenueVal?: number | null
  activeListing?: number | null
  hostLocation?: string[]
  lotoStatus?: string[]
  tinStatus?: string
  taxpayerType?: string[]
  hostProfileFilter?: 'show_all' | 'equal' | 'not_equal'
}

type ApiFilter = Filter & { isNested?: boolean; keyword?: boolean }

export const convertSTRFiltersToApiFilters = (filters: STRFilters): ApiFilter[] => {
  const apiFilters: ApiFilter[] = []

  if (filters.yearRange?.[0] && filters.yearRange?.[1]) {
    const start = (filters.yearRange[0] as { year: () => number }).year?.()
    const end = (filters.yearRange[1] as { year: () => number }).year?.()
    if (start && end) {
      apiFilters.push({
        name: 'yearRange',
        value: [String(start), String(end)],
      })
    }
  }

  if (filters.unreportedRevenueVal != null) {
    const op = filters.unreportedRevenueOp || 'gte'
    const min = op === 'gte' ? String(filters.unreportedRevenueVal) : ''
    const max = op === 'lte' ? String(filters.unreportedRevenueVal) : ''
    apiFilters.push({
      name: 'totals.taxGap',
      type: FilterTypes.RANGE,
      value: [min, max],
    })
  }

  if (filters.estimatedRevenueVal != null) {
    const op = filters.estimatedRevenueOp || 'gte'
    const min = op === 'gte' ? String(filters.estimatedRevenueVal) : ''
    const max = op === 'lte' ? String(filters.estimatedRevenueVal) : ''
    apiFilters.push({
      name: 'totals.totalRevenueDivided',
      type: FilterTypes.RANGE,
      value: [min, max],
    })
  }

  if (filters.activeListing != null) {
    apiFilters.push({
      name: 'totals.activeListings',
      type: FilterTypes.RANGE,
      value: [String(filters.activeListing), ''],
    })
  }

  if (filters.hostLocation?.length) {
    apiFilters.push({ name: 'contactInfo.addresses', value: filters.hostLocation })
  }

  if (filters.lotoStatus?.length) {
    apiFilters.push({ name: 'totals.lotoStatus', value: filters.lotoStatus })
  }

  if (filters.tinStatus) {
    apiFilters.push({ name: 'tinStatus', value: [filters.tinStatus] })
  }

  if (filters.taxpayerType?.length) {
    apiFilters.push({
      name: 'overviewDetails.activityType',
      type: FilterTypes.KEYWORD,
      value: filters.taxpayerType,
    })
  }

  if (filters.hostProfileFilter && filters.hostProfileFilter !== 'show_all') {
    apiFilters.push({ name: 'hostProfileFilter', value: [filters.hostProfileFilter] })
  }

  return apiFilters
}

const SEARCH_COLUMN_OPTIONS = [
  { label: 'Host Profile', value: 'revenueSources.profileName' },
  { label: 'Verified Name', value: 'entityDetails.name' },
  { label: 'Case ID', value: 'caseId' },
  { label: 'TIN', value: 'entityDetails.tin' },
  { label: 'Host Location', value: 'contactInfo.addresses' },
]

const FILTER_LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 400,
  fontSize: 14,
  color: '#000000D9',
  position: 'absolute',
  top: -25,
  left: 0,
}

const COMPARISON_OPTIONS = [
  { label: 'Larger than', value: 'gte' },
  { label: 'Smaller than', value: 'lte' },
]

const SeamDivider: FC = () => (
  <Divider
    type='vertical'
    style={{
      margin: 0,
      top: 0,
      height: 'auto',
      alignSelf: 'stretch',
      borderInlineStartColor: '#d9d9d9',
    }}
  />
)

export interface STRFilterOptions {
  [key: string]: { label: string; value: string }[]
}

interface STRFilterBarProps {
  isMapVisible: boolean
  onToggleMap: () => void
  filters: STRFilters
  onFiltersChange: (filters: STRFilters) => void
  onSearch: (value: string, column: string) => void
  minYear?: number | null
}

const STRFilterBar: FC<STRFilterBarProps> = ({
  isMapVisible,
  onToggleMap,
  filters,
  onFiltersChange,
  onSearch,
  minYear,
}) => {
  const [searchColumn, setSearchColumn] = useState<string>(SEARCH_COLUMN_OPTIONS[0].value)
  const [searchText, setSearchText] = useState('')

  const handleSearch = () => {
    onSearch(searchText, searchColumn)
  }

  return (
    <Flex wrap='wrap' align='center' style={{ marginBottom: 12, padding: '8px 0', columnGap: 8, rowGap: 32 }}>
      <Button icon={<Icon name='map' size='medium' className={styles.mapIcon} />} onClick={onToggleMap}>
        {isMapVisible ? 'Hide Map' : 'Show Map'}
      </Button>

      <RangePicker
        picker='year'
        style={{ width: 220 }}
        onChange={vals => {
          onFiltersChange({ ...filters, yearRange: vals ? [vals[0], vals[1]] : undefined })
        }}
        minDate={minYear ? dayjs().year(minYear).startOf('year') : undefined}
        maxDate={dayjs().endOf('year')}
        allowClear
      />

      <Space.Compact>
        <div style={{ display: 'flex' }}>
          <Select
            value={searchColumn}
            onChange={setSearchColumn}
            options={SEARCH_COLUMN_OPTIONS}
            style={{ width: 140 }}
          />
          <SeamDivider />
          <Input
            style={{ width: 160, borderRadius: 0, height: 32 }}
            placeholder='search text'
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
          />
        </div>
        <Button
          icon={<SearchOutlined style={{ color: '#00000073' }} />}
          onClick={handleSearch}
          style={{ borderLeft: 0 }}
        />
      </Space.Compact>

      <div style={{ position: 'relative' }}>
        <Typography.Text style={FILTER_LABEL_STYLE}>Unreported Revenue</Typography.Text>
        <Space.Compact>
          <Select
            value={filters.unreportedRevenueOp || 'gte'}
            onChange={val => onFiltersChange({ ...filters, unreportedRevenueOp: val as 'gte' | 'lte' })}
            options={COMPARISON_OPTIONS}
            className={styles.filterSelect}
            style={{ width: 120 }}
          />
          <SeamDivider />
          <InputNumber
            prefix='$'
            style={{ width: 100 }}
            controls={false}
            value={filters.unreportedRevenueVal}
            onChange={val => onFiltersChange({ ...filters, unreportedRevenueVal: val })}
          />
        </Space.Compact>
      </div>

      <div style={{ position: 'relative' }}>
        <Typography.Text style={FILTER_LABEL_STYLE}>Estimated Revenue</Typography.Text>
        <Space.Compact>
          <Select
            value={filters.estimatedRevenueOp || 'gte'}
            onChange={val => onFiltersChange({ ...filters, estimatedRevenueOp: val as 'gte' | 'lte' })}
            options={COMPARISON_OPTIONS}
            className={styles.filterSelect}
            style={{ width: 120 }}
          />
          <SeamDivider />
          <InputNumber
            prefix='$'
            style={{ width: 100 }}
            controls={false}
            value={filters.estimatedRevenueVal}
            onChange={val => onFiltersChange({ ...filters, estimatedRevenueVal: val })}
          />
        </Space.Compact>
      </div>

      <STRMoreFiltersPopover filters={filters} onFiltersChange={onFiltersChange} />
    </Flex>
  )
}

export default STRFilterBar
